import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Grid,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  CloudDownload,
  Delete as DeleteIcon,
  Description as DocumentIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { useDispatch } from 'react-redux';
import * as userActions from '../UserManagement.actions';
import color from 'utils/colors';

const useStyles = makeStyles(() => ({
  dialogTitle: {
    color: color.purpleSecondary,
    fontWeight: 'bold',
    fontSize: '20px',
    padding: '24px 24px 8px 24px'
  },
  dialogContent: {
    padding: '6px 24px 24px 24px',
    marginTop: '2px',
  },
  tableHeader: {
    backgroundColor: color.purpleSecondary,
    '& .MuiTableCell-head': {
      color: color.white,
      fontWeight: 'bold',
    },
  },
  addButton: {
    backgroundColor: color.purpleSecondary,
    color: color.white,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: color.purpleSecondary,
    },
  },
  actionButton: {
    color: color.purpleSecondary,
    '&:hover': {
      backgroundColor: 'rgba(149, 117, 205, 0.04)',
    },
  },
  hiddenInput: {
    display: 'none'
  }
}));

function PowersModal({ open, onClose, user, powers = [], loading = false, loadingOperations = false }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [replaceMode, setReplaceMode] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [powerToDelete, setPowerToDelete] = useState(null);
  const [deletingPower, setDeletingPower] = useState(false);

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
  };

  const isZippedFile = (fileName) => {
    const zippedExtensions = ['.zip', '.rar', '.7z', '.tar', '.gz'];
    return zippedExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
  };

  const handleAddPower = (event) => {
    const file = event.target.files[0];
    if (file && user) {
      if (!isZippedFile(file.name)) {
        // Usar la acción UserManagement específica
        dispatch(userActions.createUserPower(user.id, { 
          legalPowerFile: file
        }));
      } else {
        // TODO: Mostrar error de archivo comprimido
        console.error('El archivo no puede ser una carpeta comprimida');
      }
      event.target.value = '';
    }
  };

  const handleReplacePower = (event, powerId) => {
    const file = event.target.files[0];
    if (file && user) {
      if (!isZippedFile(file.name)) {
        // Usar la nueva acción que maneja delete + create en secuencia
        dispatch(userActions.replaceUserPower(user.id, powerId, { 
          legalPowerFile: file
        }));
      } else {
        console.error('El archivo no puede ser una carpeta comprimida');
      }
      event.target.value = '';
      setReplaceMode(null);
    }
  };

  const handleViewPower = (power) => {
    if (power.url) {
      console.log('Opening power URL:', power.url);
      window.open(power.url, '_blank');
    } else {
      console.log('URL no disponible para el poder:', power);
      // Show error notification
      dispatch({
        type: 'app/SET_ERROR',
        payload: {
          msg: 'No se pudo obtener la URL del archivo'
        }
      });
    }
  };

  const handleDeletePower = (power) => {
    setPowerToDelete(power);
    setDeleteConfirmOpen(true);
  };

  const confirmDeletePower = () => {
    if (powerToDelete && powerToDelete.id && user) {
      setDeletingPower(true);
      
      console.log('=== ELIMINANDO PODER ===');
      console.log('Poder a eliminar:', powerToDelete);
      
      // Usar la acción UserManagement específica
      dispatch(userActions.deleteUserPower(user.id, powerToDelete.id));
      
      // Cerrar modal
      setDeletingPower(false);
      setDeleteConfirmOpen(false);
      setPowerToDelete(null);
    }
  };

  const cancelDeletePower = () => {
    setDeleteConfirmOpen(false);
    setPowerToDelete(null);
  };

  if (!user) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="body"
      PaperProps={{
        sx: {
          borderRadius: '8px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        Listado de poderes
      </DialogTitle>
      
      <DialogContent className={classes.dialogContent}>
        <Box sx={{ height: '2px' }} />
        
        {/* Botón para agregar nuevo poder */}
        <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 'bold',
                  color: color.purpleSecondary,
                  textTransform: 'uppercase',
                }}
              >
                AGREGAR
              </Typography>
              <Typography
                sx={{
                  fontWeight: 'regular',
                  color: color.purpleSecondary,
                  fontSize: 16,
                }}
              >
                Poder
              </Typography>
              <label htmlFor="add-power-input">
                <input
                  accept="application/pdf"
                  id="add-power-input"
                  type="file"
                  className={classes.hiddenInput}
                  onChange={handleAddPower}
                />
                <IconButton 
                  component="span"
                  className={classes.actionButton}
                >
                  <AddIcon
                    sx={{
                      color: color.white,
                      backgroundColor: color.purpleSecondary,
                      borderRadius: 3,
                    }}
                  />
                </IconButton>
              </label>
            </Box>
          </Grid>
        </Grid>

        {/* Tabla de poderes */}
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead className={classes.tableHeader}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Poder de representación</TableCell>
                <TableCell>Tamaño</TableCell>
                <TableCell align="center">Previsualizar</TableCell>
                <TableCell align="center">Reemplazar</TableCell>
                <TableCell align="center">Descargar</TableCell>
                <TableCell align="center">Eliminar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading || loadingOperations ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                      <CircularProgress size={24} sx={{ color: color.purpleSecondary }} />
                      <Typography variant="body2" color="textSecondary">
                        {loading ? 'Cargando poderes...' : 'Procesando archivo...'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : powers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No hay poderes cargados
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                powers.map((power, index) => (
                  <TableRow key={power.id || index}>
                    <TableCell component="th" scope="row">
                      {`${index + 1}.`}
                    </TableCell>
                    <TableCell>
                      {power.content || power.name || `Poder ${index + 1}`}
                    </TableCell>
                    <TableCell align="center">
                      {power.size === 0 ? (
                        <Alert sx={{ padding: '6px' }} severity="error">
                          Error en archivo
                        </Alert>
                      ) : (
                        formatBytes(power.size || 0)
                      )}
                    </TableCell>
                    
                    {/* Columna Previsualizar */}
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        className={classes.actionButton}
                        onClick={() => handleViewPower(power)}
                        title={power.url ? "Ver en nueva pestaña" : "URL no disponible"}
                        disabled={!power.url}
                        sx={{
                          opacity: power.url ? 1 : 0.5,
                          cursor: power.url ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <DocumentIcon />
                      </IconButton>
                    </TableCell>

                    {/* Columna Reemplazar */}
                    <TableCell align="center">
                      <label htmlFor={`replace-power-${power.id || index}`}>
                        <input
                          accept="application/pdf"
                          id={`replace-power-${power.id || index}`}
                          type="file"
                          className={classes.hiddenInput}
                          onChange={(e) => handleReplacePower(e, power.id)}
                        />
                        <IconButton
                          size="small"
                          className={classes.actionButton}
                          component="span"
                          title="Reemplazar poder"
                        >
                          <UploadIcon />
                        </IconButton>
                      </label>
                    </TableCell>

                    {/* Columna Descargar */}
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        className={classes.actionButton}
                        onClick={async () => {
                          if (power.url) {
                            try {
                              // Fetch the file as blob
                              const response = await fetch(power.url);
                              const blob = await response.blob();
                              
                              // Create download link with blob URL
                              const blobUrl = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = blobUrl;
                              link.download = power.content || power.name || `poder_${power.id}.pdf`;
                              document.body.appendChild(link);
                              link.click();
                              
                              // Cleanup
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(blobUrl);
                            } catch (error) {
                              console.error('Error downloading file:', error);
                              // Fallback to original method
                              window.open(power.url, '_blank');
                            }
                          }
                        }}
                        title="Descargar"
                        disabled={!power.url || power.size === 0}
                        sx={{
                          opacity: (power.url && power.size > 0) ? 1 : 0.5,
                          cursor: (power.url && power.size > 0) ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <CloudDownload />
                      </IconButton>
                    </TableCell>

                    {/* Columna Eliminar */}
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        className={classes.actionButton}
                        onClick={() => handleDeletePower(power)}
                        title="Eliminar poder"
                        disabled={deletingPower}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      
      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button
          onClick={onClose}
          sx={{
            backgroundColor: color.purpleSecondary,
            color: color.white,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: color.purpleSecondary,
            },
          }}
          variant="contained"
        >
          Cerrar
        </Button>
      </DialogActions>

      {/* Modal de confirmación para eliminar */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={cancelDeletePower}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px'
          }
        }}
      >
        <DialogTitle sx={{ color: color.purpleSecondary, fontWeight: 'bold' }}>
          Confirmar eliminación
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ py: 2 }}>
            ¿Está seguro que desea eliminar el poder <strong>{powerToDelete?.content || powerToDelete?.name || `Poder ${powers.findIndex(p => p.id === powerToDelete?.id) + 1}`}</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={cancelDeletePower}
            sx={{
              color: color.purpleSecondary,
              borderColor: color.purpleSecondary,
              textTransform: 'none',
              '&:hover': {
                borderColor: color.purpleSecondary,
                backgroundColor: 'rgba(149, 117, 205, 0.04)',
              },
            }}
            variant="outlined"
            disabled={deletingPower}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmDeletePower}
            sx={{
              backgroundColor: color.purpleSecondary,
              color: color.white,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: color.purpleSecondary,
              },
            }}
            variant="contained"
            disabled={deletingPower}
          >
            {deletingPower ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} sx={{ color: color.white }} />
                Eliminando...
              </Box>
            ) : (
              'Eliminar'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default PowersModal;